/**
 * Revision ledger (ADR-009 §6.3): a ring of the most recent revisions with the
 * layers each one touched, fed by every history event whatever its origin.
 * Every DocumentSession attaches one when it is created, so the ledger covers
 * every revision since the document was opened.
 *
 * Transactions contribute their declared footprint and actor. Any other
 * committed command (a brush stroke, a drag, a dialog's direct execute) is
 * journaled as an implicit user transaction: its footprint comes from its
 * DamageSet and its label from the command; add-ons and action replays that
 * commit through `HistoryManager.withOrigin` are attributed to that origin.
 * The dispatcher uses the ledger to decide whether a request authored
 * against an older revision may still be compiled; the UI uses it for
 * attribution (History actor labels, the changed-by-agent marker).
 */
import type { ActorRef, LayerId, TransactionConflict } from '@maipo/agent-protocol';
import {
  AddGuideCommand,
  ClearGuidesCommand,
  QuickMaskStrokeCommand,
  SelectionCommand,
  SetActiveLayersCommand,
  SetGroupExpandedCommand,
  type Command,
  type HistoryEvent,
  type HistoryOrigin,
} from '../core/commands';
import type { DamageSet } from '../core/damage';
import { walkLayers, type EditorDocument } from '../core/model';
import type { DocumentSession } from '../core/session';
import { TransactionCommand } from './transaction';
import type { Footprint } from './registry';
import { DOCUMENT_RESOURCES } from './commands/registry';

const USER: ActorRef = Object.freeze({ kind: 'user' }) as ActorRef;

/** Last transaction and actor that changed a layer in this session (SSV `provenance`). */
export interface LayerProvenance { lastTx?: string; lastActor: ActorRef }

export interface LedgerFootprint {
  layers: LayerId[];
  structural: boolean;
  /** Document geometry (resize, crop, rotate, flip) or anything else document-wide. */
  document: boolean;
  /** The command did not say what it touched; treated as overlapping everything. */
  unknown: boolean;
}

export interface LedgerEntry {
  revision: number;
  kind: 'commit' | 'undo' | 'redo';
  actor: ActorRef;
  txId?: string;
  label: string;
  footprint: LedgerFootprint;
  /** A native command recorded as an implicit transaction (no TxMeta, footprint from damage). */
  implicit?: true;
}

function actorOfOrigin(origin: HistoryOrigin): ActorRef {
  return origin.kind === 'addon' ? { kind: 'addon', id: origin.id } : { kind: 'replay', action: origin.action };
}

/**
 * Commands that change only UI-adjacent state (pixel selection, active
 * layers, group disclosure, guides) and report no damage. Any other command
 * with empty damage is recorded as unknown, which conflicts with everything.
 */
const BENIGN_UNDAMAGED = [SelectionCommand, SetActiveLayersCommand, SetGroupExpandedCommand, AddGuideCommand, ClearGuidesCommand] as const;

export function normalizeLayerId(document: EditorDocument, id: LayerId): LayerId {
  return id === document.root.id ? 'root' : id;
}

/**
 * Native commands that change only the pixel selection. They report no
 * damage; their footprint is the `#selection` resource, so agent commands
 * that read the selection conflict with them and nothing else does.
 */
const SELECTION_ONLY = [SelectionCommand, QuickMaskStrokeCommand] as const;

function footprintFromDamage(document: EditorDocument, command: Command | undefined, damage: DamageSet): LedgerFootprint {
  const layers = [...new Set(damage.layerIds.map((id) => normalizeLayerId(document, id)))];
  if (command && SELECTION_ONLY.some((type) => command instanceof type) && layers.length === 0 && damage.tiles.length === 0 && !damage.fullDocument) {
    return { layers: [DOCUMENT_RESOURCES.selection], structural: false, document: false, unknown: false };
  }
  if (layers.length === 0 && damage.tiles.length === 0) {
    if (damage.fullDocument) return { layers, structural: damage.structural, document: true, unknown: true };
    if (command && BENIGN_UNDAMAGED.some((type) => command instanceof type)) return { layers, structural: false, document: false, unknown: false };
    return { layers, structural: damage.structural, document: false, unknown: true };
  }
  for (const tile of damage.tiles) if (!layers.includes(tile.layerId)) layers.push(normalizeLayerId(document, tile.layerId));
  return { layers, structural: damage.structural, document: false, unknown: false };
}

/**
 * What a committed transaction changed. Structural transactions also changed
 * the child lists they read (parents, placement references), so those count
 * as touched for later requests, exactly as a native structural DamageSet
 * names the parent it spliced.
 */
export function footprintFromTransaction(document: EditorDocument, footprint: Footprint, extra: readonly LayerId[] = []): LedgerFootprint {
  return {
    layers: [...new Set([...footprint.writes, ...extra, ...(footprint.structural ? footprint.reads : [])].map((id) => normalizeLayerId(document, id)))],
    structural: footprint.structural,
    document: footprint.document,
    unknown: false,
  };
}

export class RevisionLedger {
  private entries: LedgerEntry[] = [];
  /** Oldest base revision the ledger can still vouch for. */
  private floor: number;
  private readonly unsubscribe: () => void;
  /** Actors of native commands committed on behalf of someone other than the user. */
  private readonly actors = new WeakMap<Command, ActorRef>();
  private readonly provenanceByLayer = new Map<LayerId, LayerProvenance>();
  /** Size at which provenance of layers no longer in the document is dropped; doubles so pruning stays amortised. */
  private pruneAt = 1024;

  constructor(private readonly session: DocumentSession, private readonly capacity = 512) {
    this.floor = session.currentRevision;
    this.unsubscribe = session.history.subscribe((event) => this.record(event));
  }

  dispose(): void { this.unsubscribe(); }

  /** True when every revision after `baseRevision` is in the ledger. */
  covers(baseRevision: number): boolean {
    return baseRevision >= this.floor && baseRevision <= this.session.currentRevision;
  }

  entriesAfter(baseRevision: number): readonly LedgerEntry[] {
    return this.entries.filter((entry) => entry.revision > baseRevision);
  }

  latest(): LedgerEntry | undefined { return this.entries.at(-1); }

  /** Who made a history entry: a transaction's actor, an origin passed to withOrigin(), or the user. */
  actorOf(command: Command): ActorRef {
    if (command instanceof TransactionCommand) return command.meta.actor;
    return this.actors.get(command) ?? USER;
  }

  /**
   * Attributes the most recent commit to `actor` after the fact, for work
   * that commits after an await (where withOrigin() cannot be used safely).
   * Only applies while `command` is still that commit.
   */
  reattributeLatest(command: Command, actor: ActorRef): void {
    const entry = this.entries.at(-1);
    if (command instanceof TransactionCommand || !entry || entry.kind !== 'commit' || entry.revision !== this.session.currentRevision || this.session.history.top() !== command) return;
    this.actors.set(command, actor);
    entry.actor = actor;
    this.markProvenance(entry.footprint.layers, actor, undefined);
  }

  /** The revision at which transaction `txId` last took effect (commit or redo), if the ledger still holds it. */
  revisionOf(txId: string): number | undefined {
    for (let index = this.entries.length - 1; index >= 0; index -= 1) {
      const entry = this.entries[index];
      if (entry?.txId === txId && entry.kind !== 'undo') return entry.revision;
    }
    return undefined;
  }

  /** Session provenance of a layer: the last transaction and actor that changed it. */
  provenance(layerId: LayerId): LayerProvenance | undefined {
    return this.provenanceByLayer.get(layerId);
  }

  private markProvenance(layers: readonly LayerId[], actor: ActorRef, txId: string | undefined): void {
    for (const id of layers) if (id !== 'root') this.provenanceByLayer.set(id, txId === undefined ? { lastActor: actor } : { lastTx: txId, lastActor: actor });
  }

  /**
   * Drops provenance for layers that left the document, so a long session
   * with heavy layer churn stays bounded by the live layer count. A layer
   * restored later by undo simply has no provenance until its next edit.
   */
  private pruneProvenance(): void {
    if (this.provenanceByLayer.size <= this.pruneAt) return;
    const present = new Set(walkLayers(this.session.document.root).map((layer) => layer.id));
    for (const id of this.provenanceByLayer.keys()) if (!present.has(id)) this.provenanceByLayer.delete(id);
    this.pruneAt = Math.max(1024, this.provenanceByLayer.size * 2);
  }

  private record(event: HistoryEvent): void {
    if (event.kind === 'reset') {
      this.entries = [];
      this.floor = event.revision;
      this.provenanceByLayer.clear();
      return;
    }
    if (event.kind !== 'commit' && event.kind !== 'undo' && event.kind !== 'redo') return;
    const command = event.command;
    const document = this.session.document;
    let entry: LedgerEntry;
    if (command instanceof TransactionCommand) {
      const created = Object.values(command.meta.aliases);
      entry = {
        revision: event.revision,
        kind: event.kind,
        actor: command.meta.actor,
        txId: command.meta.txId,
        label: command.label,
        footprint: footprintFromTransaction(document, command.meta.footprint, created),
      };
      // Provenance names the layers the transaction wrote or created, not the parents it only read.
      const written = [...new Set([...command.meta.footprint.writes, ...created].map((id) => normalizeLayerId(document, id)))];
      if (event.kind === 'undo') {
        for (const id of written) if (this.provenanceByLayer.get(id)?.lastTx === command.meta.txId) this.provenanceByLayer.delete(id);
      } else {
        this.markProvenance(written, command.meta.actor, command.meta.txId);
      }
    } else {
      if (command && event.kind === 'commit' && event.origin) this.actors.set(command, actorOfOrigin(event.origin));
      const actor = command ? this.actorOf(command) : USER;
      entry = { revision: event.revision, kind: event.kind, actor, label: command?.label ?? 'Edit', footprint: footprintFromDamage(document, command, event.damage), implicit: true };
      if (event.kind !== 'undo') this.markProvenance(entry.footprint.layers, actor, undefined);
    }
    if (event.kind !== 'commit') entry.label = `${event.kind === 'undo' ? 'Undo' : 'Redo'} ${entry.label}`;
    this.entries.push(entry);
    this.pruneProvenance();
    if (this.entries.length > this.capacity) {
      const dropped = this.entries.shift();
      if (dropped) this.floor = dropped.revision;
    }
  }
}

export interface ConcurrencyInput {
  ledger: RevisionLedger;
  document: EditorDocument;
  baseRevision: number;
  currentRevision: number;
  mode: 'scoped' | 'strict';
  footprint: Footprint;
}

export type ConcurrencyOutcome =
  | { ok: true }
  | { ok: false; code: 'STALE_REVISION' | 'CONFLICT'; message: string; conflicts: TransactionConflict[] };

function ancestorsOf(document: EditorDocument, ids: readonly LayerId[]): Set<LayerId> {
  const wanted = new Set(ids);
  const result = new Set<LayerId>();
  const visit = (children: readonly EditorDocument['root']['children'][number][], trail: LayerId[]) => {
    for (const layer of children) {
      if (wanted.has(layer.id)) for (const ancestor of trail) result.add(ancestor);
      if (layer.kind === 'group') visit(layer.children, [...trail, layer.id]);
    }
  };
  visit(document.root.children, []);
  return result;
}

function conflictOf(entry: LedgerEntry): TransactionConflict {
  return { revision: entry.revision, actor: entry.actor, label: entry.label, layers: entry.footprint.layers.filter((id) => id !== 'root') };
}

/**
 * Scoped optimistic concurrency (ADR-009 §6.3). Never merges: a request
 * either compiles against the current document because nothing it depends on
 * moved, or it is refused with the revisions that got in the way.
 */
export function checkConcurrency(input: ConcurrencyInput): ConcurrencyOutcome {
  const { ledger, document, baseRevision, currentRevision, mode, footprint } = input;
  if (baseRevision === currentRevision) return { ok: true };
  const intervening = baseRevision < currentRevision ? ledger.entriesAfter(baseRevision) : [];
  if (baseRevision > currentRevision) {
    return { ok: false, code: 'STALE_REVISION', message: `baseRevision ${baseRevision} is ahead of the document (revision ${currentRevision}).`, conflicts: [] };
  }
  if (mode === 'strict') {
    return { ok: false, code: 'STALE_REVISION', message: `Strict concurrency: the document is at revision ${currentRevision}, not ${baseRevision}.`, conflicts: intervening.map(conflictOf) };
  }
  if (!ledger.covers(baseRevision)) {
    return { ok: false, code: 'STALE_REVISION', message: `Revision ${baseRevision} is older than the revision ledger; inspect again.`, conflicts: [] };
  }
  const touched = new Set([...footprint.reads, ...footprint.writes].map((id) => normalizeLayerId(document, id)));
  const ancestors = ancestorsOf(document, [...touched]);
  const conflicts = intervening.filter((entry) => {
    const print = entry.footprint;
    // A document-wide request (geometry) depends on every layer, so any intervening revision conflicts.
    if (footprint.document || print.unknown || print.document) return true;
    if (print.layers.some((id) => touched.has(id))) return true;
    return print.structural && print.layers.some((id) => ancestors.has(id));
  });
  if (conflicts.length === 0) return { ok: true };
  return {
    ok: false,
    code: 'CONFLICT',
    message: `The transaction's layers were changed after revision ${baseRevision} (revision${conflicts.length === 1 ? '' : 's'} ${conflicts.map((entry) => entry.revision).join(', ')}). Inspect again and retry; nothing was changed.`,
    conflicts: conflicts.map(conflictOf),
  };
}
